import { useState } from "react";
import type { Account, AccountInput, AccountType, Currency } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import { Modal } from "./Modal";
import { MoneyField } from "./fields";
import { Button } from "./primitives";

const TYPES: { value: AccountType; th: string; en: string }[] = [
  { value: "thai_fund", th: "กองทุนไทย", en: "Thai fund" },
  { value: "thai_stock_set", th: "หุ้นไทย (SET)", en: "Thai stock (SET)" },
  { value: "us_stock_etf", th: "หุ้น/ETF สหรัฐ", en: "US stock / ETF" },
  { value: "cash", th: "เงินสด", en: "Cash" },
  { value: "other", th: "อื่น ๆ", en: "Other" },
];

export function AccountForm({
  open,
  mode,
  currency,
  account,
  onClose,
  onSave,
  onDelete,
  saving = false,
}: {
  open: boolean;
  mode: "add" | "edit";
  currency: Currency;
  account?: Account;
  onClose: () => void;
  onSave: (input: AccountInput) => void;
  onDelete?: () => void;
  saving?: boolean;
}) {
  const { t, locale } = useLanguage();
  const [name, setName] = useState(account?.name ?? "");
  const [type, setType] = useState<AccountType>(account?.type ?? "cash");
  const [value, setValue] = useState(account?.value.amountMinorUnits ?? 0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const valid = name.trim().length > 0 && value >= 0;

  return (
    <Modal open={open} onClose={onClose} title={mode === "add" ? t("addAccount") : t("editAccount")}>
      <div className="field">
        <label htmlFor="acc-name">{t("name")}</label>
        <div className="field__control">
          <input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: "0 0 16px" }}>
        <legend className="panel__title">{t("type")}</legend>
        {TYPES.map((tp) => (
          <label key={tp.value} style={{ display: "flex", gap: 8, alignItems: "center", minHeight: 44 }}>
            <input
              type="radio"
              name="account-type"
              value={tp.value}
              checked={type === tp.value}
              onChange={() => setType(tp.value)}
            />
            {locale === "th" ? tp.th : tp.en}
          </label>
        ))}
      </fieldset>

      <MoneyField
        label={t("currentValue")}
        amountMinorUnits={value}
        currency={currency}
        onChange={setValue}
      />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        {mode === "edit" && onDelete && !confirmDelete && (
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            {t("delete")}
          </Button>
        )}
        {confirmDelete && onDelete && (
          <span role="alertdialog" aria-live="assertive" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {t("confirmDelete")}
            <Button variant="danger" onClick={onDelete}>
              {t("delete")}
            </Button>
          </span>
        )}
        <Button
          variant="primary"
          loading={saving}
          disabled={!valid}
          onClick={() => onSave({ name: name.trim(), type, value: { amountMinorUnits: value, currency } })}
        >
          {t("save")}
        </Button>
      </div>
    </Modal>
  );
}
